import {
  folderLabel,
  isBriefFolder,
  isIssueAttachmentsFolder,
} from "@/domains/documents/utils/briefFolder";
import {
  ChevronDown,
  ChevronRight,
  ClipboardList,
  FolderIcon,
  FolderOpen,
  ListTodo,
} from "lucide-react";
import type { TreeFolder } from "@/domains/documents/types";
import { cn } from "@/lib/utils";

interface PickerFolderTreeProps {
  folders: TreeFolder[];
  selectedFolderId: string | null;
  expandedFolderIds: ReadonlySet<string>;
  onSelect: (folderId: string) => void;
  onToggle: (folderId: string) => void;
}

interface PickerFolderTreeNodeProps extends Omit<PickerFolderTreeProps, "folders"> {
  folder: TreeFolder;
  level: number;
}

function FolderTreeToggle({
  folderId,
  hasChildren,
  isExpanded,
  onToggle,
}: {
  folderId: string;
  hasChildren: boolean;
  isExpanded: boolean;
  onToggle: (folderId: string) => void;
}) {
  if (!hasChildren) return <span className="inline-block w-4" />;
  return (
    <button
      onClick={(event) => {
        event.stopPropagation();
        onToggle(folderId);
      }}
      className="rounded p-0.5 hover:bg-muted"
    >
      {isExpanded ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
    </button>
  );
}

function PickerFolderTreeNode({
  folder,
  level,
  selectedFolderId,
  expandedFolderIds,
  onSelect,
  onToggle,
}: PickerFolderTreeNodeProps) {
  const isExpanded = expandedFolderIds.has(folder.id);
  const isSelected = selectedFolderId === folder.id;

  return (
    <div>
      <div
        className={cn(
          "flex cursor-pointer items-center gap-1 rounded px-2 py-1.5 text-sm hover:bg-muted/50",
          isSelected && "bg-accent"
        )}
        style={{ paddingLeft: `${8 + level * 16}px` }}
        onClick={() => onSelect(folder.id)}
      >
        <FolderTreeToggle
          folderId={folder.id}
          hasChildren={folder.children.length > 0}
          isExpanded={isExpanded}
          onToggle={onToggle}
        />
        {isBriefFolder(folder) ? (
          <ClipboardList className="h-4 w-4 shrink-0 text-primary" aria-hidden="true" />
        ) : isIssueAttachmentsFolder(folder) ? (
          <ListTodo className="h-4 w-4 shrink-0 text-primary" aria-hidden="true" />
        ) : isSelected ? (
          <FolderOpen className="h-4 w-4 shrink-0 text-primary" />
        ) : (
          <FolderIcon className="h-4 w-4 shrink-0 text-primary" />
        )}
        <span className="truncate text-left">{folderLabel(folder)}</span>
      </div>
      {isExpanded && folder.children.length > 0 && (
        <PickerFolderTree
          folders={folder.children}
          selectedFolderId={selectedFolderId}
          expandedFolderIds={expandedFolderIds}
          onSelect={onSelect}
          onToggle={onToggle}
          level={level + 1}
        />
      )}
    </div>
  );
}

export function PickerFolderTree({
  folders,
  selectedFolderId,
  expandedFolderIds,
  onSelect,
  onToggle,
  level = 0,
}: PickerFolderTreeProps & { level?: number }) {
  return (
    <>
      {folders.map((folder) => (
        <PickerFolderTreeNode
          key={folder.id}
          folder={folder}
          level={level}
          selectedFolderId={selectedFolderId}
          expandedFolderIds={expandedFolderIds}
          onSelect={onSelect}
          onToggle={onToggle}
        />
      ))}
    </>
  );
}
